import React, { useCallback, useMemo } from "react";
import {useSelector} from "react-redux";
import {Translation as T} from "@awe-web/shared/lib";
import {provisioningTypes} from "@awe-web/shared/lib";
import {StationAddress} from "../../stations/components/station_card.jsx";
import {getPaymentDetails} from "../../user/store.js";
import {getCiStation} from "../../stations/store.js";
import {
  getProvisioningDetailsB2b,
  getProvisioningDetailsB2c,
  getDriver
} from "../store.js";

import "./rent_contract.scss";

// ======================================================================
// Helper
// ======================================================================

function Line({value}) {
  return value
  ? <div>{value}</div>
  : null;
}

function LabeledLine({label, value}) {
  return value
  ? <div>{label}: {value}</div>
  : null;
}

// ======================================================================
// PaymentDetails
// ======================================================================

const PAYMENT_SEPA = "ALG0000097";
const PAYMENT_ON_ACCOUNT = "ALG0000098";
const ACCOUNT_HOLDER = "ALG0000099";
const IBAN = "ALG0000100";
const BIC = "ALG0000101";

export function PaymentDetails() {
  const statePaymentDetails = useSelector((state) => getPaymentDetails(state));
  const paymentDetails = useMemo(() => statePaymentDetails ? statePaymentDetails.toJS() : {}, [statePaymentDetails]);

  const typeHeading = useCallback(() => {
    return paymentDetails?.type === "SEPA" ? (<T code={PAYMENT_SEPA} />) : (<T code={PAYMENT_ON_ACCOUNT} />);
  }, [paymentDetails?.type]);

  return (
    <div className="payment-details">
      <div className="payment-details__type">
        {typeHeading()}
      </div>
      <div>
        <T code={ACCOUNT_HOLDER} />: {paymentDetails?.accountHolder}
      </div>
      <div>
        <T code={IBAN} />: {paymentDetails?.iban}
      </div>
      <div>
        <T code={BIC} />: {paymentDetails?.bic}
      </div>
      <div>
        {paymentDetails?.bank}
      </div>
    </div>
  );
}

// ======================================================================
// DriverDetails
// ======================================================================

const EMPLOYEE_ID = "ALG0000102";
const COST_CENTER = "ALG0000103";

export function DriverDetails() {
  const stateDriverDetails = useSelector((state) => getDriver(state));
  const driverDetails = useMemo(() => stateDriverDetails ? stateDriverDetails.toJS() : {}, [stateDriverDetails]);

  return (
    <div className="driver-details">
      <div className="driver-details__name">
        {driverDetails?.fullName}
      </div>
      <Line value={driverDetails?.email} />
      <Line value={driverDetails?.phone} />
      <LabeledLine label={<T code={EMPLOYEE_ID} />} value={driverDetails?.employeeId} />
      <LabeledLine label={<T code={COST_CENTER} />} value={driverDetails?.costCenter} />
    </div>
  );
}

// ======================================================================
// ProvisioningDetails
// ======================================================================

export function ProvisioningDetails({reservationPath}) {
  
  const b2bProvisioningDetails = useSelector((state) => getProvisioningDetailsB2b(state));
  const b2cProvisioningDetails = useSelector((state) => getProvisioningDetailsB2c(state));
  
  const provisioningDetails = useMemo(() => {
    return reservationPath === "B2B" ? b2bProvisioningDetails.toJS() : b2cProvisioningDetails.toJS();
  }, [b2bProvisioningDetails, b2cProvisioningDetails, reservationPath]);
  
  if (provisioningDetails?.provisioningType === provisioningTypes.PICKUP) {
    return (
      <div className="provisioning-details">
        <div className="provisioning-details__type">{provisioningDetails?.label}</div>
        <StationAddress
          station={provisioningDetails?.pickupStation}
          stationNameClassName="provisioning-details__station-address__name"
          openingTimesHeadingClassName="provisioning-details__station-address__opening-times-heading"
        />
      </div>
    );
  }
  else {
    const deliveryAddress = provisioningDetails?.deliveryAddress;

    return (
      <div className="provisioning-details">
        <div className="provisioning-details__type">
          {deliveryAddress?.label}
        </div>
        <div>
          {deliveryAddress?.companyName}
        </div>
        <Line value={deliveryAddress?.fullName} />
        <div>
          {deliveryAddress?.street}
        </div>
        <Line value={deliveryAddress?.building} />
        <div>
          {deliveryAddress?.zipCode} {deliveryAddress?.city}
        </div>
        <Line value={deliveryAddress?.country} />
        <Line value={deliveryAddress?.phonenumber} />
      </div>
    );
  }
}

// ======================================================================
// ReturnDetails
// ======================================================================

const RETURN_AT_STATION = "ALG0000242";

export function ReturnDetails() {
  const stateReturnStation = useSelector((state) => getCiStation(state));
  const returnStation = useMemo(() => stateReturnStation ? stateReturnStation.toJS() : {}, [stateReturnStation]);

  return (
    <div className="return-details">
      <div className="return-details__type">
        <T code={RETURN_AT_STATION} />
      </div>
      <StationAddress
        station={returnStation}
        stationNameClassName="return-details__station-address__name"
        openingTimesHeadingClassName="return-details__station-address__opening-times-heading"
      />
    </div>
  );
}
